'use client';

import { type FC, useState } from 'react';
import { cn } from '../../../utils/cn';
import { Avatar, Spinner, EmptyState } from '../../ui';
import { Credits } from '../../../components/Credits';
import { ScreenContent, ScreenFixedTop } from '../../../screen';
import { FaTrophy } from '../../icons';
import { RushMidTabsBar } from './shared/RushMidTabsBar';
import type { RushMidTab } from './RushMidGame';
import { useRushMidRating, type RushMidRatingMode } from '../../../queries/useRushMidQueries';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import { useNavigation } from '../../../contexts/NavigationContext';
import { rushMidAvatarUrl } from './avatarUrl';
import { formatCreditsNumber } from '../../../utils/formatters';
import translations from './RushMid.translations';
import type { RushMidRatingResponse, RushMidRatingEntry } from '../../../services/generated/Api';

type Translate = ReturnType<typeof useLocalTranslation>['t'];

const MODES: { mode: RushMidRatingMode; key: 'mode1' | 'mode2' | 'mode5' }[] = [
  { mode: 1, key: 'mode1' },
  { mode: 2, key: 'mode2' },
  { mode: 5, key: 'mode5' },
];

const MEDAL_COLOR: Record<number, string> = {
  1: 'bg-gradient-to-b from-[#FFD75E] to-[#E0A100] text-black',
  2: 'bg-gradient-to-b from-[#D8DEE6] to-[#9AA3AD] text-black',
  3: 'bg-gradient-to-b from-[#E8A06A] to-[#B06A35] text-black',
};


/** First word → firstName, rest → lastName for the avatar initials fallback. */
function splitName(name: string): { firstName: string; lastName?: string } {
  const [first, ...rest] = name.trim().split(/\s+/);
  return { firstName: first, lastName: rest.length ? rest.join(' ') : undefined };
}

interface RankBadgeProps {
  rank: number;
}

const RankBadge: FC<RankBadgeProps> = ({ rank }) => {
  const medal = MEDAL_COLOR[rank];
  if (!medal) {
    return <span className="w-5 shrink-0 text-center text-sm font-semibold tabular-nums text-label-secondary">{rank}</span>;
  }
  return (
    <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold', medal)}>
      {rank}
    </span>
  );
};

const Stat: FC<{ label: string; value: string; tone?: 'green' | 'red' }> = ({ label, value, tone }) => (
  <div className="flex flex-1 flex-col items-center gap-0.5">
    <span
      className={cn(
        'text-base font-bold tabular-nums',
        tone === 'green' && 'text-accent-green',
        tone === 'red' && 'text-accent-red',
        !tone && 'text-label-primary',
      )}
    >
      {value}
    </span>
    <span className="text-[11px] text-label-secondary">{label}</span>
  </div>
);

const YourRatingCard: FC<{ you: RushMidRatingEntry; t: Translate }> = ({ you, t }) => {
  const { firstName, lastName } = splitName(you.name);
  return (
    <div className="rounded-2xl bg-bg-secondary p-4">
      <div className="flex items-center gap-4">
        <div className="relative shrink-0">
          <Avatar
            size="l"
            src={rushMidAvatarUrl(you.userId)}
            fallbackKey={you.userId}
            firstName={firstName}
            lastName={lastName}
          />
          <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 rounded-full bg-accent-blue px-2 py-0.5 text-xs font-bold text-white ring-2 ring-bg-secondary">
            #{you.rank}
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-label-secondary">{t('rating')}</span>
            <span className="inline-flex items-center gap-1 text-base font-bold tabular-nums text-label-primary">
              {formatCreditsNumber(you.rating)}
              <FaTrophy size={13} className="text-gold" />
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-label-secondary">{t('earned')}</span>
            <Credits amount={you.coins} iconSize={13} className="text-base font-bold tabular-nums text-label-primary" />
          </div>
        </div>
      </div>
      <div className="mt-4 flex items-stretch gap-2">
        <Stat label={t('playedShort')} value={String(you.played)} />
        <Stat label={t('winsShort')} value={String(you.wins)} tone="green" />
        <Stat label={t('lossesShort')} value={String(you.losses)} tone="red" />
        <Stat label="K/D" value={(you.kills / Math.max(1, you.deaths)).toFixed(2)} />
        <Stat label={t('winrate')} value={`${you.winrate.toFixed(1)}%`} />
      </div>
    </div>
  );
};

const UnratedCard: FC<{ t: Translate }> = ({ t }) => (
  <div className="rounded-2xl bg-bg-secondary p-6 text-center">
    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/5 text-label-secondary">
      <FaTrophy size={22} />
    </div>
    <div className="mt-3 text-base font-semibold text-label-primary">{t('ratingUnratedTitle')}</div>
    <div className="mt-1 text-sm text-label-secondary">{t('ratingUnratedDescription')}</div>
  </div>
);

const TopRow: FC<{ entry: RushMidRatingEntry; isYou: boolean; onOpen?: (userId: string) => void }> = ({ entry, isYou, onOpen }) => {
  const { firstName, lastName } = splitName(entry.name);
  return (
    <li
      onClick={onOpen ? () => onOpen(entry.userId) : undefined}
      className={cn(
        'flex items-center gap-2 border-t border-white/5 px-3 py-2.5 text-sm first:border-t-0',
        isYou && 'bg-white/5',
        onOpen && 'cursor-pointer transition-opacity active:opacity-70',
      )}
    >
      <RankBadge rank={entry.rank} />
      <Avatar
        size="s"
        src={rushMidAvatarUrl(entry.userId)}
        fallbackKey={entry.userId}
        firstName={firstName}
        lastName={lastName}
      />
      <span className="min-w-0 flex-1 truncate text-left font-semibold text-label-primary">{entry.name}</span>
      <span className="inline-flex w-12 shrink-0 items-center justify-center font-bold tabular-nums text-label-primary">
        {formatCreditsNumber(entry.rating)}
      </span>
      <span className="w-7 shrink-0 text-center font-bold tabular-nums text-label-primary">{entry.played}</span>
      <span className="w-7 shrink-0 text-center font-bold tabular-nums text-accent-green">{entry.wins}</span>
      <span className="w-7 shrink-0 text-center font-bold tabular-nums text-accent-red">{entry.losses}</span>
      <span className="w-9 shrink-0 text-center font-bold tabular-nums text-label-primary">
        {(entry.kills / Math.max(1, entry.deaths)).toFixed(2)}
      </span>
    </li>
  );
};

export interface RatingScreenViewProps {
  data?: RushMidRatingResponse;
  isLoading?: boolean;
  mode: RushMidRatingMode;
  onMode: (mode: RushMidRatingMode) => void;
  tab?: RushMidTab;
  onTab?: (tab: RushMidTab) => void;
  onOpenProfile?: (userId: string) => void;
}

/** Presentational rating + leaderboard — data passed in (Storybook / tests). */
export const RatingScreenView: FC<RatingScreenViewProps> = ({ data, isLoading, mode, onMode, tab, onTab, onOpenProfile }) => {
  const { t } = useLocalTranslation(translations);
  const you = data?.you ?? null;
  const top = data?.top;
  const youUserId = you?.userId;

  return (
    <>
      <ScreenFixedTop title={t('lobbyTitle')}>
        {tab && onTab && <RushMidTabsBar activeTab={tab} onChange={onTab} />}
      </ScreenFixedTop>
      <ScreenContent hideLegalSection scrollKey="rush-mid-rating">
        <div className="flex flex-col gap-3 px-4 pt-3 pb-6">
          <div>
            <div className="text-sm font-medium text-label-secondary">{t('format')}</div>
            <div className="mt-2 flex gap-2">
              {MODES.map((m) => (
                <button
                  key={m.mode}
                  onClick={() => onMode(m.mode)}
                  className={cn(
                    'flex-1 rounded-xl border py-2.5 text-[15px] font-semibold transition-colors',
                    mode === m.mode
                      ? 'border-accent-blue bg-accent-blue/15 text-label-primary'
                      : 'border-white/10 bg-bg-secondary text-label-secondary',
                  )}
                >
                  {t(m.key)}
                </button>
              ))}
            </div>
          </div>

          {isLoading && !data ? (
            <div className="flex min-h-40 items-center justify-center">
              <Spinner size="l" />
            </div>
          ) : (
            <>
              <div>
                <div className="mb-2 text-sm font-medium text-label-secondary">{t('yourRating')}</div>
                {you ? <YourRatingCard you={you} t={t} /> : <UnratedCard t={t} />}
              </div>

              <div>
                <div className="mb-2 text-sm font-medium text-label-secondary">{t('topPlayers')}</div>
                {!top || top.length === 0 ? (
                  <EmptyState title={t('ratingEmptyTitle')} description={t('ratingEmptyDescription')} />
                ) : (
                  <div className="overflow-hidden rounded-2xl bg-bg-secondary">
                    <div className="flex items-center gap-2 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-label-secondary">
                      <span className="w-5 shrink-0 text-center">#</span>
                      <span className="w-7 shrink-0" />
                      <span className="flex-1 text-left">{t('player')}</span>
                      <span className="w-12 shrink-0 text-center">{t('rating')}</span>
                      <span className="w-7 shrink-0 text-center">{t('playedShort')}</span>
                      <span className="w-7 shrink-0 text-center">{t('winsShort')}</span>
                      <span className="w-7 shrink-0 text-center">{t('lossesShort')}</span>
                      <span className="w-9 shrink-0 text-center">K/D</span>
                    </div>
                    <ul>
                      {top.map((entry) => (
                        <TopRow key={entry.userId} entry={entry} isYou={entry.userId === youUserId} onOpen={onOpenProfile} />
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </ScreenContent>
    </>
  );
};

export interface RatingScreenProps {
  tab?: RushMidTab;
  onTab?: (tab: RushMidTab) => void;
}

/** Container — owns the format state + fetch, renders the presentational view. */
export const RatingScreen: FC<RatingScreenProps> = ({ tab, onTab }) => {
  const [mode, setMode] = useState<RushMidRatingMode>(1);
  const query = useRushMidRating(mode);
  const navigation = useNavigation();

  return (
    <RatingScreenView
      data={query.data}
      isLoading={query.isLoading}
      mode={mode}
      onMode={setMode}
      tab={tab}
      onTab={onTab}
      onOpenProfile={(userId) => navigation.navigate('profile', { userId })}
    />
  );
};
